import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { practitionerMappingApi, type StatsResponse } from '../api/practitionerMappingApi';
import { statusMappingApi } from '../api/statusMappingApi';
import { appointmentTypeMappingApi } from '../api/appointmentTypeMappingApi';
import { serviceMappingApi } from '../api/serviceMappingApi';
import { useLocationContext } from './LocationContext';

export type EntityKey = 'appointments' | 'providers' | 'status' | 'services';

type EntityStatsMap = Record<EntityKey, StatsResponse | null>;

const EMPTY_STATS: EntityStatsMap = { appointments: null, providers: null, status: null, services: null };

interface EntityStatsContextValue {
    stats: EntityStatsMap;
    loading: boolean;
    /** Re-fetch the stats (e.g. after a cleanup changed the suggestions). */
    refresh: () => void;
}

const EntityStatsContext = createContext<EntityStatsContextValue | null>(null);

/** Fetches each entity's mapping stats once per location change, shared by the sidebar badges and the dashboard coverage cards. */
export const EntityStatsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { locationId } = useLocationContext();
    const [stats, setStats] = useState<EntityStatsMap>(EMPTY_STATS);
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const refresh = useCallback(() => setReloadKey(k => k + 1), []);

    useEffect(() => {
        if (!locationId) {
            setStats(EMPTY_STATS);
            return;
        }
        let cancelled = false;
        setLoading(true);

        Promise.allSettled([
            appointmentTypeMappingApi.getStats(locationId),
            practitionerMappingApi.getStats(locationId),
            statusMappingApi.getStats(locationId),
            serviceMappingApi.getStats(locationId),
        ]).then(([appointments, providers, status, services]) => {
            if (cancelled) return;
            setStats({
                appointments: appointments.status === 'fulfilled' ? appointments.value : null,
                providers: providers.status === 'fulfilled' ? providers.value : null,
                status: status.status === 'fulfilled' ? status.value : null,
                services: services.status === 'fulfilled' ? services.value : null,
            });
            setLoading(false);
        });

        return () => { cancelled = true; };
    }, [locationId, reloadKey]);

    return (
        <EntityStatsContext.Provider value={{ stats, loading, refresh }}>
            {children}
        </EntityStatsContext.Provider>
    );
};

export function useEntityStats(): EntityStatsContextValue {
    const ctx = useContext(EntityStatsContext);
    if (!ctx) throw new Error('useEntityStats must be used within an EntityStatsProvider');
    return ctx;
}
